// @flow
import React, { PureComponent } from "react";
import { compose } from "redux";
import DocViewer from "components/DocViewer";
import InlineViewer from "components/InlineViewer";
import LoadingIframe from "components/LoadingIframe";
import { fetchDownloadToken } from "data/api";
import {
    LoadingType,
    ServerEventActionType,
    ServerEventItemType,
    type DocInfoResultWithTokenDTO,
    DocsFlags,
} from "data/types";
import Status from "components/Status";
import Constants, { type ConstantsEnum } from "data/constants";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import Loader from "components/Loader";
import withOfflineHandler from "containers/OfflineHandler";

/* istanbul ignore next */
const MediaViewer = Loader(
    {
        loader: () =>
            import(
                "components/MediaViewer" /* webpackChunkName: "component-MediaViewer" */
            ),
    },
    LoadingType.Status
);

/* istanbul ignore next */
const ImageViewer = Loader(
    {
        loader: () =>
            import(
                "components/ImageViewer" /* webpackChunkName: "component-ImageViewer" */
            ),
    },
    LoadingType.Status
);

type Props = {
    /** current itemUri to display */
    itemUri?: ?string,
    /** callback when we have docInfo
     * @param {DocInfoResultWithTokenDTO} docInfo document info result
     */
    onDocInfoResult?: (docInfo: DocInfoResultWithTokenDTO) => void,
    /** callback when user clicks left arrow */
    onPrev?: () => void,
    /** callback when user clicks right arrow */
    onNext?: () => void,
    /** GlobalEventsHandler.refreshId */
    refreshId: number,
    /**pdf.js: search terms */
    searchTerm?: string,
    /** pdf.js: whether trigger browser print */
    triggerPrint: boolean,
};

type State = {
    status: ConstantsEnum,
    currentViewer: string,
    pdf: boolean,
    itemUri: ?string,
    isDocDownloadAllowed: boolean,
    token: ?string,
    customViewerUrl: ?string,
};

export class DocViewerContainer extends PureComponent<Props, State> {
    state = {
        status: Constants.LOADING,
        currentViewer: "pdfJs",
        pdf: true,
        itemUri: null,
        isDocDownloadAllowed: false,
        token: null,
        customViewerUrl: null,
    };

    componentDidMount() {
        this._fetch(this.props.itemUri);
    }

    componentDidUpdate(prevProps: Props): void {
        /* istanbul ignore else */
        if (
            prevProps.itemUri !== this.props.itemUri ||
            prevProps.refreshId !== this.props.refreshId
        ) {
            this._fetch(this.props.itemUri);
        }
    }

    _fetch = (itemUri?: ?string): void => {
        /* istanbul ignore else */
        if (itemUri != null) {
            this.setState({ status: Constants.LOADING }, () =>
                this.props.offlineHandler
                    .factory(fetchDownloadToken)(itemUri)
                    .catch((err) => {
                        this.setState({ status: Constants.ERROR });
                        return null;
                    })
                    .then((result: DocInfoResultWithTokenDTO) => {
                        if (result !== null) {
                            const { onDocInfoResult } = this.props;
                            if (typeof onDocInfoResult === "function")
                                onDocInfoResult(result);
                            this.setState(
                                this._getUsableViewer(result, "pdfJs", itemUri)
                            );
                        }
                    })
            );
        }
    };

    _getUsableViewer = (
        result: DocInfoResultWithTokenDTO,
        docViewer: "inline" | "pdfJs",
        itemUri: ?string
    ): State => {
        const isDocDownloadAllowed = !(
            ~result.flags & DocsFlags.IsDocDownloadAllowed
        );
        if (Object.prototype.hasOwnProperty.call(result, "customViewerUrl")) {
            return {
                currentViewer: "customViewerUrl",
                customViewerUrl: result.customViewerUrl,
                pdf: false,
                status: Constants.OK,
                itemUri,
                isDocDownloadAllowed,
            };
        }
        // #42627 integrated image viewer
        if (
            window.CONFIG.general.imageViewerExtensions.includes(
                String(result.ext).toLowerCase()
            )
        ) {
            return {
                token: result.token,
                currentViewer: "image",
                pdf: false,
                status: Constants.OK,
                itemUri,
                isDocDownloadAllowed,
            };
        }

        if (
            window.CONFIG.general.mediaViewerExtensions.includes(
                String(result.ext).toLowerCase()
            )
        ) {
            return {
                token: result.token,
                currentViewer: "media",
                pdf: false,
                status: Constants.OK,
                itemUri,
                isDocDownloadAllowed,
            };
        }

        if (
            docViewer === "pdfJs" &&
            !result.isPdf &&
            !result.hasPdf &&
            !result.canPdf
        ) {
            console.warn(`is/has/canPdf is false for ${result.name})`);
            // will fallback to inline and show original file
            return {
                token: result.token,
                currentViewer: "inline",
                pdf: false,
                status: Constants.OK,
                itemUri,
                isDocDownloadAllowed,
            };
        } else {
            return {
                token: result.token,
                currentViewer: docViewer,
                pdf: true,
                status: Constants.OK,
                itemUri,
                isDocDownloadAllowed,
            };
        }
    };

    render() {
        const { pdf, status, itemUri } = this.state;
        /* istanbul ignore else */
        if (itemUri == null || status !== Constants.OK)
            return (
                <Status
                    status={status}
                    error={this.props.offlineHandler.error}
                />
            );

        /* istanbul ignore next */
        switch (this.state.currentViewer) {
            case "customViewerUrl":
                return (
                    <LoadingIframe
                        title="CustomViewer"
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allowFullScreen={true}
                        src={this.state.customViewerUrl}
                    />
                );
            case "image":
                return (
                    <ImageViewer
                        token={this.state.token}
                        onPrev={this.props.onPrev}
                        onNext={this.props.onNext}
                    />
                );
            case "media":
                return (
                    <MediaViewer
                        token={this.state.token}
                        onPrev={this.props.onPrev}
                        onNext={this.props.onNext}
                    />
                );
            case "inline":
                return (
                    <InlineViewer
                        itemUri={itemUri}
                        isDocDownloadAllowed={this.state.isDocDownloadAllowed}
                        onPrev={this.props.onPrev}
                        onNext={this.props.onNext}
                    />
                );
            case "pdfJs":
                return (
                    <DocViewer
                        pdf={pdf}
                        token={this.state.token}
                        onPrev={this.props.onPrev}
                        onNext={this.props.onNext}
                        searchTerm={this.props.searchTerm}
                        triggerPrint={this.props.triggerPrint}
                    />
                );
            default:
                alert(
                    `Unknown docViewer: ${window.CONFIG.general.docViewer} !`
                );
                break;
        }
    }
}

export default compose(
    GlobalEventsHandler({
        actions: [
            ServerEventActionType.modify,
            ServerEventActionType.edit,
            ServerEventActionType.refresh,
        ],
        items: [ServerEventItemType.document],
    }),
    withOfflineHandler
)(DocViewerContainer);
